import { useNavigate, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button } from "antd";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import creditCardIcon from "../../../../Assets/Icons/credit_card.svg";
import approvedImage from "../../../../Assets/Icons/verified.svg";
import refusedImage from "../../../../Assets/Icons/verified_off.svg";
import "./style.css";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import { getAdjustmentById } from "../../../../API/FuelVendor/TransactionAdjustments";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveFileUrl = (path) => {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  let normalized = path.startsWith("/") ? path : `/${path}`;
  return `${BACKEND_ORIGIN}${normalized}`;
};

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const formatLabel = (str) =>
  str
    ? str
        .split("_")
        .map((word) => capitalize(word))
        .join(" ")
    : str;

const formatDate = (isoDate) => {
  if (!isoDate) return "-";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const TransactionAdjustmentsDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const adjustmentId = location.state?.id;

  const [adjustment, setAdjustment] = useState(null);

  useEffect(() => {
    if (!adjustmentId) return;

    const fetchAdjustment = async () => {
      try {
        const res = await getAdjustmentById(adjustmentId);

        if (Math.floor(res?.status / 100) === 2) {
          setAdjustment(res?.data?.data || null);
        }
      } catch (err) {
        console.error("Failed to load transaction adjustment:", err);
      }
    };

    fetchAdjustment();
  }, [adjustmentId]);

  const status = capitalize(adjustment?.status) || "-";
  const isApproved = status === "Approved";
  const isPending = status === "Pending";

  const documents = (adjustment?.documents || []).map((doc) => ({
    key: String(doc.id),
    name: doc.document_name || "-",
    category: doc.category || "-",
    date: formatDate(doc.uploaded_at),
    expiryDate: doc.expiry_date,
    status: "Verified",
    fileUrl: resolveFileUrl(doc.file),
  }));

  const handleBack = () => {
    navigate("/transactions-vendor", {
      state: { activeTab: location.state?.from || "Transaction Adjustments" },
    });
  };

  return (
    <div className="fv-txn-detail">
      <div className="fv-txn-detail__back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>

      <div className="fv-txn-detail__header">
        <div className="fv-txn-detail__title">
          {adjustment?.adj_id || "-"} / {adjustment?.txn_id || "-"}
        </div>
        <span
          className={`fv-txn-detail__badge ${isApproved ? "approved" : isPending ? "pending" : "denied"}`}
        >
          <img
            src={isApproved ? approvedImage : refusedImage}
            alt={status}
          />
          {status}
        </span>
      </div>

      <div className="fv-txn-detail__subheader">
        <img
          src={creditCardIcon}
          alt=""
          className="fv-txn-detail__subheader-icon"
        />
        <span className="fv-txn-detail__subheader-label">
          Fleet Company:{" "}
          <span className="fv-txn-detail__subheader-value">
            {adjustment?.fleet_company_name || "-"}
          </span>
        </span>
      </div>

      <div className="fv-txn-detail__stats">
        <GenericStatCard
          title="Original Amount"
          value={formatCurrency(adjustment?.original_amount)}
          showBlankLine={false}
        />
        <GenericStatCard
          title="New Amount"
          value={formatCurrency(adjustment?.new_amount)}
          showBlankLine={false}
        />
        <GenericStatCard
          title="Net Adjustment"
          value={formatCurrency(adjustment?.net_diff)}
          showBlankLine={false}
        />
        <GenericStatCard
          title="Adjustment Type"
          value={formatLabel(adjustment?.type) || "-"}
          showBlankLine={false}
        />
      </div>
      <div className="adjustment-reason">
        <div className="heading">Reason for Adjustment</div>
        <div className="content">{adjustment?.reason || "-"}</div>
      </div>

      {adjustment?.decision_notes && (
        <div className="adjustment-reason">
          <div className="heading">Decision Notes</div>
          <div className="content">{adjustment.decision_notes}</div>
        </div>
      )}

      <div>
        <GenericDocumentManagerTable
          documentTypes={["Transaction Receipt"]}
          initialDocuments={documents}
          showTableOnly={true}
          hideRemoveAction={true}
        />
      </div>
    </div>
  );
};

export default TransactionAdjustmentsDetail;
